import {
    Component,
    Object3D,
    InputComponent,
    PhysXComponent,
} from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';
import { Locomotion } from './locomotion.js';

/**
 * SnapRotate component handles player rotation in discrete increments (snap rotation)
 * based on input from XR controllers.
 *
 * @remarks
 * Requires an InputComponent on the same object and a PhysXComponent on the physxObject.
 */
export class SnapRotate extends Component {
    static TypeName = 'snap-rotate';

    /**
     * The PhysX-enabled object to apply rotation to.
     *
     * @requires PhysXComponent
     */
    @property.object()
    physxObject?: Object3D;

    /** Rotation speed applied during snap rotation */
    @property.int(5)
    angularVelocity = 5;

    /** Reference to the locomotion object controlling player movement */
    @property.object({ required: true })
    locomotionObject!: Object3D;

    /** Input component for reading XR controller input */
    private input?: InputComponent;

    /** Flag to prevent continuous snapping without releasing the input */
    private snapped = false;

    /** Lower threshold to reset snapping state */
    private lowThreshold = 0.2;

    /** Higher threshold to trigger snap rotation */
    private highThreshold = 0.5;

    /** PhysX component for applying physics-based rotation */
    private physxComponent?: PhysXComponent;

    /** Locomotion component for managing player rotation state */
    private locomotionComponent?: Locomotion;

    /** Queue of rotation actions to execute sequentially */
    private queue = new Array<() => void>();

    /**
     * Initializes component references and validates required components.
     */
    start() {
        // Validate and assign InputComponent
        const tempInput = this.object.getComponent(InputComponent);
        if (!tempInput) {
            throw new Error(
                'The snap-rotate component requires an InputComponent on the same object.'
            );
        }
        this.input = tempInput;

        // Validate physxObject and PhysXComponent
        if (!this.physxObject) {
            throw new Error('snap-rotate: physxObject property must be set.');
        }
        const tempPhysxComponent =
            this.physxObject.getComponent(PhysXComponent);
        if (!tempPhysxComponent) {
            throw new Error(
                'The snap-rotate component requires a PhysXComponent on physxObject.'
            );
        }
        this.physxComponent = tempPhysxComponent;

        // Validate and assign Locomotion component
        const tempLocomotionComponent =
            this.locomotionObject.getComponent(Locomotion);
        if (!tempLocomotionComponent) {
            throw new Error(
                'snap-rotate: locomotionObject does not have a Locomotion component.'
            );
        }
        this.locomotionComponent = tempLocomotionComponent;

        this.snapped = false;
    }

    /**
     * Handles snap rotation logic based on XR controller input.
     */
    update() {
        // Execute queued actions (e.g., toggling kinematic state)
        // if (this.queue.length > 0) {
        //     const exec = this.queue.pop();
        //     if (exec) {
        //         exec();
        //         return;
        //     }
        // }

        // Read current axis input from XR controller
        const currentAxis = this.input?.xrInputSource?.gamepad?.axes?.[2];
        if (currentAxis === undefined) {
            return;
        }

        // Reset snapped state if input is below low threshold
        if (Math.abs(currentAxis) < this.lowThreshold) {
            this.snapped = false;
            return;
        }

        // Prevent snapping again until input is released
        if (this.snapped || Math.abs(currentAxis) < this.highThreshold) {
            return;
        }

        // Mark as snapped to prevent repeated snapping
        this.snapped = true;

        // Determine rotation direction based on input axis
        const rotationAngle = currentAxis > 0 ? -45 : 45;

        // Queue actions to toggle kinematic state and apply rotation
        // this.queue.push(() => {
        //     this.physxComponent!.kinematic = false;
        //     this.locomotionComponent!.isRotating = false;
        // });
        // this.queue.push(() => {
        this.physxObject!.rotateAxisAngleDegObject([0, 1, 0], rotationAngle);
        //});

        // Set kinematic state and rotation flag
        //this.physxComponent!.kinematic = true;
        this.locomotionComponent!.isRotating = true;
    }
}
